/**
 * AI 다이어그램 반응형 스케일 mixin (#663, #715)
 *
 * 사용처: MdSb.vue (`.mark-down-sb`), HtmlText.vue (`#v-html-text`)
 *
 * 배경
 *  AI가 작성하는 ESG 시각화 HTML은 인라인 `width: NNNpx` / `display: flex` 등
 *  고정 픽셀 기반 레이아웃을 자주 포함한다. 클래스명·구조가 일관되지 않아
 *  CSS 셀렉터로 일괄 오버라이드가 불가능하므로, 자연 폭이 컨테이너 폭을
 *  초과하는 직계 자식만 비율만큼 zoom으로 축소한다.
 *
 * 동작
 *  - mounted / 콘텐츠 변경 / 컨테이너 리사이즈 시 직계 자식 요소 측정
 *  - scrollWidth > clientWidth 인 경우만 zoom = clientWidth / scrollWidth
 *  - 컨테이너 폭이 NARROW_CONTAINER_THRESHOLD 이상이면 zoom 해제 →
 *    기존 가로 스크롤(.md-wrapper) 동작 유지 (#715 — 뷰포트가 아닌 컨테이너
 *    기준으로 판단해야 번역페이지처럼 데스크톱에서도 컬럼 폭이 좁은 케이스에
 *    대응 가능)
 *  - 표/SVG/이미지처럼 다른 단계에서 max-width: 100%가 적용된 요소는
 *    scrollWidth == clientWidth가 되어 자동으로 통과
 *  - ResizeObserver/리사이즈 콜백은 requestAnimationFrame으로 coalesce하여
 *    회전·드래그 리사이즈 시 jank 방지
 *
 * 명명 규칙
 *  - mixin 전용 인스턴스 프로퍼티/메서드는 Vue 공식 스타일 가이드 권장에 따라
 *    `$_diagramScale_` 네임스페이스 prefix 사용 (다른 mixin/컴포넌트 충돌 방지)
 *
 * 한계
 *  - zoom 속성은 W3C CSS Spec 드래프트 단계. Chrome/Safari/Edge는 항상,
 *    Firefox는 126(2024-05)+에서 지원.
 *  - 최상위 직계 자식만 측정하므로, 중첩된 작은 박스 안에 큰 다이어그램이
 *    있는 경우 부모 박스의 자연 폭이 그 다이어그램 만큼 늘어나야 적용됨.
 *
 * @param {Object} options
 * @param {string} options.refName - 대상 컨테이너 ref 이름 (예: 'mdRoot', 'htmlRoot')
 * @param {string} options.contentWatchProp - 콘텐츠 변경 감지 computed 이름
 */
export function createDiagramScaleMixin({ refName, contentWatchProp } = {}) {
  // 컨테이너 폭 임계값 (Bootstrap lg). 컨테이너가 이보다 좁을 때만 zoom 적용 —
  // 그 이상이면 가로 스크롤로 처리(기존 동작). 뷰포트가 아닌 컨테이너 폭을
  // 기준으로 삼아야 번역페이지처럼 좁은 컬럼 케이스도 처리된다.
  const NARROW_CONTAINER_THRESHOLD = 992;

  const mixin = {
    mounted() {
      this.$nextTick(() => this.$_diagramScale_schedule());
      this.$_diagramScale_observe();
    },
    beforeDestroy() {
      this.$_diagramScale_unobserve();
    },
    watch: {},
    methods: {
      // rAF coalescing — 동일 프레임 내 다중 호출은 하나로 합침
      $_diagramScale_schedule() {
        if (this.$_diagramScale_rafId) return;
        this.$_diagramScale_rafId = window.requestAnimationFrame(() => {
          this.$_diagramScale_rafId = null;
          this.$_diagramScale_apply();
        });
      },
      $_diagramScale_apply() {
        const root = this.$refs[refName];
        if (!root) return;
        const targetParent = root.querySelector('.md-wrapper') || root;
        const container = targetParent.clientWidth;
        const isWideContainer = container >= NARROW_CONTAINER_THRESHOLD;

        Array.from(targetParent.children).forEach(child => {
          if (child.nodeType !== 1) return;
          // 측정 전 zoom 초기화 (이전 스케일 영향 제거)
          child.style.zoom = '';
          if (isWideContainer) return;

          const natural = child.scrollWidth;
          if (natural > 0 && container > 0 && natural > container) {
            child.style.zoom = (container / natural).toFixed(3);
          }
        });
      },
      $_diagramScale_observe() {
        if (typeof window === 'undefined') return;
        const root = this.$refs[refName];
        const onResize = () => this.$_diagramScale_schedule();
        if (window.ResizeObserver && root) {
          this.$_diagramScale_observer = new ResizeObserver(onResize);
          this.$_diagramScale_observer.observe(root);
        } else {
          this.$_diagramScale_resizeHandler = onResize;
          window.addEventListener('resize', this.$_diagramScale_resizeHandler);
        }
      },
      $_diagramScale_unobserve() {
        if (this.$_diagramScale_rafId) {
          window.cancelAnimationFrame(this.$_diagramScale_rafId);
          this.$_diagramScale_rafId = null;
        }
        if (this.$_diagramScale_observer) {
          this.$_diagramScale_observer.disconnect();
          this.$_diagramScale_observer = null;
        }
        if (this.$_diagramScale_resizeHandler) {
          window.removeEventListener('resize', this.$_diagramScale_resizeHandler);
          this.$_diagramScale_resizeHandler = null;
        }
      }
    }
  };

  if (contentWatchProp) {
    mixin.watch[contentWatchProp] = function () {
      this.$nextTick(() => this.$_diagramScale_schedule());
    };
  }

  return mixin;
}
